import { useCallback, useEffect, useMemo, useState } from "react";

import { hasAIJob, useAIJobPolling } from "@hooks/useAIJobPolling";
import { getCourseAIJobs } from "@views/Skills/api";

import { Course } from "types/entities";
import { AIJob, AIJobProgress, AIJobType } from "types/entities/AIJobs";

interface AICourseState {
  resetCourseAIState: () => void;
  isGeneratingImage: boolean;
  imageProgress?: AIJobProgress;
  isTranslatingCourse: boolean;
  translationProgress?: AIJobProgress;
}

export function useAICourseState(course?: Course): AICourseState {
  const courseId = course?.id;

  const hasJob = useCallback(
    (type: AIJobType): boolean => hasAIJob(type, course?.ai_jobs ?? []),
    [course],
  );
  const findJob = useCallback(
    (type: AIJobType): AIJob | undefined =>
      (course?.ai_jobs ?? []).find((job: AIJob) => job.type === type),
    [course],
  );

  // Initial state based on existing jobs
  const [isGeneratingImage, setIsGeneratingImage] = useState(hasJob("ai_course_generate_image"));
  const [imageProgress, setImageProgress] = useState<AIJobProgress | undefined>(
    findJob("ai_course_generate_image")?.progress,
  );
  const [isTranslatingCourse, setIsTranslatingCourse] = useState(
    hasJob("ai_course_clone_and_translate"),
  );
  const [translationProgress, setTranslationProgress] = useState<AIJobProgress | undefined>(
    findJob("ai_course_clone_and_translate")?.progress,
  );

  const onError = useCallback((): void => {
    setIsGeneratingImage(false);
    setIsTranslatingCourse(false);
    setImageProgress(undefined);
  }, [setIsGeneratingImage, setIsTranslatingCourse, setImageProgress]);

  // Polling setup: only enable when we have a valid courseId
  const pollingEnabled = Boolean(courseId);
  const fetcher = useCallback(
    (signal?: AbortSignal) =>
      courseId
        ? getCourseAIJobs(courseId, signal)
        : Promise.resolve({ _meta: {}, _data: [] as AIJob[] }),
    [courseId],
  );

  const filters: AIJobType[] = useMemo(() => {
    return ["ai_course_generate_image", "ai_course_clone_and_translate"];
  }, []);

  const {
    ai_course_generate_image: courseImageJob,
    ai_course_clone_and_translate: courseTranslationJob,
    startPolling,
  } = useAIJobPolling(pollingEnabled, fetcher, filters, onError);

  // Sync state with polling jobs
  useEffect(() => {
    setIsGeneratingImage(Boolean(courseImageJob));
    setImageProgress(courseImageJob?.progress);
  }, [courseImageJob]);

  useEffect(() => {
    setIsTranslatingCourse(Boolean(courseTranslationJob));
    setTranslationProgress(courseTranslationJob?.progress);
  }, [courseTranslationJob]);

  // Re-run initial hasJob when course.ai_jobs changes
  useEffect(() => {
    setIsGeneratingImage(hasJob("ai_course_generate_image"));
    setImageProgress(findJob("ai_course_generate_image")?.progress);
    setIsTranslatingCourse(hasJob("ai_course_clone_and_translate"));
    setTranslationProgress(findJob("ai_course_clone_and_translate")?.progress);
  }, [findJob, hasJob]);

  useEffect(() => {
    if (course?.id && (isGeneratingImage || isTranslatingCourse)) {
      startPolling();
    }
  }, [course?.id, isGeneratingImage, isTranslatingCourse, startPolling]);

  return useMemo(() => {
    return {
      resetCourseAIState: startPolling,
      isGeneratingImage,
      imageProgress,
      isTranslatingCourse,
      translationProgress,
    };
  }, [startPolling, isGeneratingImage, imageProgress, isTranslatingCourse, translationProgress]);
}
